import { format, parse, isValid as isValidDate, startOfWeek, getWeekOfMonth } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { nlBE as Locale } from 'date-fns/locale/nl-BE';
import { Time } from './time';
import z from 'zod';

/** We currently only support `Europe/Brussels` as timezone */
export namespace DateTimeSDK {
    export const LOCALE = 'nl-BE';
    export const TIME_ZONE = 'Europe/Brussels';

    /** A `TZDate` that has been initialized with the `Europe/Brussels` timezone */
    export type BRUDate = TZDate;

    /** Creates a `TZDate` from the given input */
    export function bruDate(
        data:
            | Time.MS
            | Date
            | (
                & Pick<DateTimeSDK.ISODate.Data, 'year'> // Required
                & Partial<DateTimeSDK.ISODate.Data>
                & Partial<DateTimeSDK.ISOTime.Data>
                & {
                    seconds?: number;
                    ms?: number;
                }
            ) = new Date() /* NOW */, // prettier-ignore
    ): BRUDate {
        if (typeof data === 'number') return new TZDate(data, TIME_ZONE);
        if (data instanceof Date) return new TZDate(data, TIME_ZONE);

        const { year, month = 1, day = 1, hours = 0, minutes = 0, seconds = 0, ms = 0 } = data;
        if (month < 1 || month > 12) {
            throw new Error(`Invalid nth month "${month}". Should be between 1 and 12`);
        }
        if (day < 1 || day > 31) {
            throw new Error(`Invalid nth day "${day}". Should be between 1 and 31`);
        }

        return new TZDate(year, month - 1, day, hours, minutes, seconds, ms, TIME_ZONE);
    }

    export namespace BRUDate {
        export function getNow() {
            return bruDate();
        }

        export function fromISOParts(
            isoDate: ISODate | ISODate.Data,
            isoTime?: ISOTime | ISOTime.Data | null,
        ): BRUDate;
        export function fromISOParts(
            isoDate: string | ISODate | ISODate.Data,
            isoTime?: string | ISOTime | ISOTime.Data | null,
        ): BRUDate | null;
        export function fromISOParts(
            isoDate: string | ISODate | ISODate.Data,
            isoTime?: string | ISOTime | ISOTime.Data | null,
        ): BRUDate | null {
            try {
                return bruDate({
                    ...(typeof isoDate === 'string' ? ISODate.decode(isoDate as any) : isoDate),
                    ...(typeof isoTime === 'string' ? ISOTime.decode(isoTime as any) : isoTime),
                });
            } catch (e) {
                console.error({ isoDate, isoTime }, e);
                return null;
            }
        }

        export function beginOfYear(year: number) {
            return DateTimeSDK.bruDate({ year });
        }

        export function endOfYear(year: number) {
            // prettier-ignore
            return DateTimeSDK.bruDate((
                DateTimeSDK.BRUDate
                    .beginOfYear(year + 1)
                    .valueOf() - 1
            ) satisfies number as Time.MS);
        }
    }

    export function isValid(date: Date): boolean {
        return !Number.isNaN(date.valueOf());
    }

    function createDateTimeFormat(options: Omit<Intl.DateTimeFormatOptions, 'timeZone'>) {
        return Intl.DateTimeFormat(LOCALE, {
            timeZone: TIME_ZONE,
            ...options,
        });
    }

    /** Formats to `yyyy-MM-dd` */
    export function formatNativeDate(date: Date): typeof NATIVE_INPUT_DATE_FORMAT {
        return format(date, NATIVE_INPUT_DATE_FORMAT, {
            locale: Locale,
        }) as typeof NATIVE_INPUT_DATE_FORMAT;
    }
    const NATIVE_INPUT_DATE_FORMAT = 'yyyy-MM-dd';

    const DD_MM_YYYY = createDateTimeFormat({
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    /** Formats the given `date` to `dd/mm/yyyy` inside the `Brussels/Europe` timezone
     * @example `15/03/2025` */
    export function toLocaleDateStr(date: Date) {
        return DD_MM_YYYY.format(date) as `${number /* dd */}/${number /* mm */}/${number /* yyyy */}`; // prettier-ignore
    }

    const HH_MM = createDateTimeFormat({
        hour: '2-digit',
        hour12: false,
        minute: '2-digit',
    });
    /** Formats the given `date` to `hh:mm` inside the `Brussels/Europe` timezone
     * @example `11:30` */
    export function toLocaleTimeStr(date: Date) {
        return HH_MM.format(date) as `${number /* hh */}:${number /* mm */}`;
    }

    /** Formats the given `date` to `dd/MM/yyyy hh:mm` inside the `Brussels/Europe` timezone.
     * @example `15/03/2025 11:30` */
    export function toLocaleDateTimeStr(date: Date) {
        return `${DateTimeSDK.toLocaleDateStr(date)} ${DateTimeSDK.toLocaleTimeStr(date)}`;
    }

    const DD_MM = createDateTimeFormat({
        day: '2-digit',
        month: '2-digit',
    });

    /** Formats the given `date` to `dd/mm` inside the `Brussels/Europe` timezone
     * @example `15/03` */
    export function toLocaleDayMonthStr(date: Date) {
        return DD_MM.format(date) as `${number /* dd */}/${number /* mm */}`;
    }

    const SHORT_MONTH = createDateTimeFormat({
        month: 'short',
    });

    /** Formats the given `date` to short month name inside the `Brussels/Europe` timezone
     * @example `Jan`, `Feb` */
    export function toLocaleShortMonthStr(date: Date) {
        return SHORT_MONTH.format(date);
    }

    /** ISO date yyyy-MM-dd */
    export type ISODate = `${number}-${number}-${number}`;
    export namespace ISODate {
        export type Data = z.output<typeof CODEC.out>;

        export const CODEC = z.codec(
            z.iso.date() as z.ZodType<ISODate, ISODate>, // https://zod.dev/api#iso-dates
            z.object({
                year: z.int(),
                /** nth month (1..=12) */
                month: z.int().min(1).max(12),
                /** nth day (1..=31) */
                day: z.int().min(1).max(31),
            }),
            {
                decode(isoDate) {
                    const [yyyy, MM, dd] = isoDate.split('-').map(Number);

                    return {
                        year: yyyy!,
                        month: MM!,
                        day: dd!,
                    };
                },
                encode(data) {
                    return `${data.year.toString().padStart(4, '0') as `${number}`
                        }-${data.month.toString().padStart(2, '0') as `${number}`
                        }-${data.day.toString().padStart(2, '0') as `${number}`
                        }` as const; // prettier-ignore
                },
            },
        );

        /** Encodes the given `data` into an ISO date.
         * NOTE: it's timezone unaware */
        export function encode(data: ISODate.Data | Date): ISODate {
            return CODEC.encode(data instanceof Date ? parseDate_unchecked(data) : data);
        }

        /** Encodes the given `date` from an ISO date.
         * NOTE: it's timezone unaware */
        export function decode(date: ISODate | Date): ISODate.Data {
            if (typeof date === 'string') return CODEC.decode(date);

            return CODEC.out.decode(parseDate_unchecked(date));
        }

        function parseDate_unchecked(date: Date): ISODate.Data {
            return {
                year: date.getFullYear(),
                month: date.getMonth() /* idx */ + 1,
                day: date.getDate() /* nth */,
            };
        }
    }

    /** ISO time hh:mm */
    export type ISOTime = `${number}:${number}`;
    export namespace ISOTime {
        export type Data = z.output<typeof CODEC.out>;

        export const CODEC = z.codec(
            z.iso.time({
                precision: -1, // HH:MM (minute precision)
            }) as z.ZodType<ISOTime, ISOTime>, // https://zod.dev/api#iso-times
            z.object({
                hours: z.int().min(0).max(24),
                minutes: z.int().min(0).max(60),
            }),
            {
                decode(isoTime) {
                    const [hh, mm] = isoTime.split(':').map(Number);

                    return {
                        hours: hh!,
                        minutes: mm!,
                    };
                },
                encode(data) {
                    return `${data.hours.toString().padStart(2, '0') as `${number}`
                        }:${data.minutes.toString().padStart(2, '0') as `${number}`
                        }` as const; // prettier-ignore
                },
            },
        );

        /** Encodes the given `data` into an ISO time.
         * NOTE: it's timezone unaware */
        export function encode(data: ISOTime.Data | Date): ISOTime {
            return CODEC.encode(data instanceof Date ? parseDate_unchecked(data) : data);
        }

        /** Encodes the given `date` from an ISO date.
         * NOTE: it's timezone unaware */
        export function decode(date: ISOTime | Date): ISOTime.Data {
            if (typeof date === 'string') return CODEC.decode(date);

            return CODEC.out.decode(parseDate_unchecked(date));
        }

        function parseDate_unchecked(date: Date): ISOTime.Data {
            return {
                hours: date.getHours(),
                minutes: date.getMinutes(),
            };
        }

        /** @example ```typescript
         * parseToMinutes('01:30') === 90
         * ``` */
        export function parseToMinutes(timestamp: string): number | null {
            const validated = ISOTime.CODEC.in.safeParse(timestamp);
            if (!validated.success) return null;

            const parsed = ISOTime.CODEC.safeDecode(validated.data);
            if (!parsed.success) return null;

            return parsed.data.hours * 60 + parsed.data.minutes;
        }
    }

    /** Parse `DD/MM/YYYY` format */
    export function parseFromDDMMYYYY(value: string): Date | undefined {
        const trimmed = value.trim();
        if (!trimmed) return undefined;

        const parsed = parse(trimmed, 'dd/MM/yyyy', new Date());

        // Validate that it's a real date and matches the input exactly
        // (date-fns parse can sometimes "wrap" invalid dates like Feb 30th)
        if (!isValidDate(parsed) || format(parsed, 'dd/MM/yyyy') !== trimmed) {
            return undefined;
        }

        return parsed;
    }

    /** Time grouping options */
    export type GroupBy = 'day' | 'week' | 'month' | 'quarter';

    /**
     * Get a unique key for grouping a date by the specified grouping type
     */
    export function getGroupKey(date: Date, groupBy: GroupBy): string {
        switch (groupBy) {
            case 'week':
                return format(date, "yyyy-'W'ww");
            case 'month':
                return format(date, 'yyyy-MM');
            case 'quarter':
                return format(date, "yyyy-'Q'Q");
            default:
                return format(date, 'yyyy-MM-dd');
        }
    }

    /**
     * Format a group key into a human-readable label for the X-axis
     * Week format: "Jan W1", "Jan W2", "Feb W1", etc.
     */
    export function formatGroupLabel(key: string, groupBy: GroupBy, _locale?: string): string {
        switch (groupBy) {
            case 'week': {
                // Key format: "2024-W01"
                const [yearStr, weekPart] = key.split('-W');
                const year = parseInt(yearStr!, 10);
                const isoWeek = parseInt(weekPart!, 10);

                // Calculate approximate date from ISO week to get the month
                // ISO week 1 contains Jan 4, so we calculate from there
                const jan4 = new Date(year, 0, 4);
                const startOfYearWeek = startOfWeek(jan4, { weekStartsOn: 1 });
                const weekDate = new Date(startOfYearWeek);
                weekDate.setDate(weekDate.getDate() + (isoWeek - 1) * 7);

                const monthName = DateTimeSDK.toLocaleShortMonthStr(weekDate);
                const weekInMonth = getWeekOfMonth(weekDate, { weekStartsOn: 1 });

                return `${monthName} W${weekInMonth}`;
            }
            case 'month': {
                // Key format: "2024-01"
                const [yearStr, monthStr] = key.split('-');
                const date = new Date(parseInt(yearStr!, 10), parseInt(monthStr!, 10) - 1, 1);
                return DateTimeSDK.toLocaleShortMonthStr(date);
            }
            case 'quarter': {
                // Key format: "2024-Q1"
                const [yearStr, quarterStr] = key.split('-Q');
                const year = parseInt(yearStr!, 10);
                const quarter = parseInt(quarterStr!, 10);
                return `${year} Q${quarter}`;
            }
            default:
                return key;
        }
    }
}
